import type { Directive, DirectiveBinding } from 'vue'

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left'

interface TooltipOptions {
  text: string
  position?: TooltipPosition
}

interface TooltipState {
  text: string
  position: TooltipPosition
  tip: HTMLElement | null
  show: () => void
  hide: () => void
}

const OFFSET = 8

function resolveOptions(binding: DirectiveBinding<string | TooltipOptions>): {
  text: string
  position: TooltipPosition
} {
  const value = binding.value
  const argPosition = binding.arg as TooltipPosition | undefined

  if (typeof value === 'string') {
    return { text: value, position: argPosition ?? 'bottom' }
  }
  return { text: value?.text ?? '', position: value?.position ?? argPosition ?? 'bottom' }
}

function positionTip(el: HTMLElement, tip: HTMLElement, position: TooltipPosition) {
  const rect = el.getBoundingClientRect()
  const tipRect = tip.getBoundingClientRect()

  let top = 0
  let left = 0

  switch (position) {
    case 'top':
      top = rect.top - tipRect.height - OFFSET
      left = rect.left + rect.width / 2 - tipRect.width / 2
      break
    case 'bottom':
      top = rect.bottom + OFFSET
      left = rect.left + rect.width / 2 - tipRect.width / 2
      break
    case 'left':
      top = rect.top + rect.height / 2 - tipRect.height / 2
      left = rect.left - tipRect.width - OFFSET
      break
    case 'right':
      top = rect.top + rect.height / 2 - tipRect.height / 2
      left = rect.right + OFFSET
      break
  }

  // Keep the tooltip inside the viewport
  left = Math.max(4, Math.min(left, window.innerWidth - tipRect.width - 4))
  top = Math.max(4, Math.min(top, window.innerHeight - tipRect.height - 4))

  tip.style.top = `${top}px`
  tip.style.left = `${left}px`
}

function getState(el: HTMLElement): TooltipState | undefined {
  return (el as any)._tooltipState
}

/**
 * Vue directive that shows a tooltip for an element on hover/focus.
 * The tooltip is appended to document.body so it is never clipped by
 * overflow containers, and the text is mirrored to aria-label for
 * screen readers when the element has no accessible name.
 *
 * Usage:
 * v-tooltip="'Open settings'"                          - bottom placement (default)
 * v-tooltip:top="'Pause mission'"                      - placement via argument
 * v-tooltip="{ text: 'Go to dock', position: 'top' }"  - full options
 */
export const vTooltip: Directive<HTMLElement, string | TooltipOptions> = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string | TooltipOptions>) {
    const state: TooltipState = {
      ...resolveOptions(binding),
      tip: null,
      show: () => {
        if (!state.text || state.tip) return
        const tip = document.createElement('div')
        tip.className = 'v-tooltip'
        tip.setAttribute('role', 'tooltip')
        tip.textContent = state.text
        document.body.appendChild(tip)
        state.tip = tip
        positionTip(el, tip, state.position)
      },
      hide: () => {
        state.tip?.remove()
        state.tip = null
      },
    }
    ;(el as any)._tooltipState = state

    if (state.text && !el.getAttribute('aria-label') && !el.textContent?.trim()) {
      el.setAttribute('aria-label', state.text)
    }

    el.addEventListener('mouseenter', state.show)
    el.addEventListener('mouseleave', state.hide)
    el.addEventListener('focusin', state.show)
    el.addEventListener('focusout', state.hide)
    // A click usually opens a panel/modal or removes the button - dismiss the tooltip
    el.addEventListener('click', state.hide)
  },

  updated(el: HTMLElement, binding: DirectiveBinding<string | TooltipOptions>) {
    const state = getState(el)
    if (!state) return
    const { text, position } = resolveOptions(binding)
    state.text = text
    state.position = position
    if (state.tip) {
      state.tip.textContent = text
      positionTip(el, state.tip, position)
    }
  },

  unmounted(el: HTMLElement) {
    const state = getState(el)
    if (!state) return
    state.hide()
    el.removeEventListener('mouseenter', state.show)
    el.removeEventListener('mouseleave', state.hide)
    el.removeEventListener('focusin', state.show)
    el.removeEventListener('focusout', state.hide)
    el.removeEventListener('click', state.hide)
    delete (el as any)._tooltipState
  },
}

export default vTooltip
